import { useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useIsStandalone } from '@ui/utils/useIsStandalone';

export interface PwaBackButtonProps {
  className?: string;
}

/**
 * A back button that only appears when the app is running as a standalone PWA.
 * In standalone mode, there are no browser navigation controls, so this provides
 * users with a way to navigate back.
 *
 * The button only appears when:
 * 1. The app is running in standalone PWA mode
 * 2. The user is not on the home page (pathname !== '/')
 */
export function PwaBackButton({ className }: PwaBackButtonProps) {
  const isStandalone = useIsStandalone();
  const navigate = useNavigate();
  const location = useLocation();

  const handleBack = useCallback(() => {
    // If there's history, go back; otherwise navigate to home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  }, [navigate]);

  // Don't render if not in standalone mode
  if (!isStandalone) {
    return null;
  }

  // Don't render on the home page - nowhere to go back to
  if (location.pathname === '/') {
    return null;
  }

  const buttonClassName = `pwa-back-button ${className || ''}`.trim();

  return (
    <button className={buttonClassName} onClick={handleBack} aria-label="Go back" type="button">
      <svg
        className="pwa-back-button__icon"
        width="24"
        height="24"
        viewBox="0 0 25 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15.5 6L9.5 12L15.5 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
